import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import { Building2, Users, Eye, Phone, Handshake, Flame, TrendingUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [hotLeads, setHotLeads] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, l, lh] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/listings?mine=true"),
          api.get("/leads?tag=hot"),
        ]);
        setStats(s.data);
        setRecent(l.data.slice(0, 4));
        setHotLeads(lh.data.slice(0, 5));
      } catch {}
    })();
  }, []);

  const kpis = [
    { label: "Total Listing", value: stats?.total_listings ?? "—", icon: Building2, accent: "text-blue-300", bg: "from-blue-500/10 to-blue-500/0" },
    { label: "Listing Aktif", value: stats?.active_listings ?? "—", icon: TrendingUp, accent: "text-emerald-300", bg: "from-emerald-500/10 to-emerald-500/0" },
    { label: "Total Lead", value: stats?.total_leads ?? "—", icon: Users, accent: "text-slate-200", bg: "from-slate-500/10 to-slate-500/0" },
    { label: "Hot Leads", value: stats?.hot_leads ?? "—", icon: Flame, accent: "text-red-400", bg: "from-red-500/10 to-red-500/0" },
    { label: "Total Views", value: stats?.total_views ?? "—", icon: Eye, accent: "text-orange-300", bg: "from-orange-500/10 to-orange-500/0" },
    { label: "WA Clicks", value: stats?.whatsapp_clicks ?? "—", icon: Phone, accent: "text-emerald-300", bg: "from-emerald-500/10 to-emerald-500/0" },
    { label: "Co-Broke", value: stats?.cobroke_count ?? "—", icon: Handshake, accent: "text-orange-300", bg: "from-orange-500/10 to-orange-500/0" },
    { label: "SOLD", value: stats?.sold_listings ?? "—", icon: TrendingUp, accent: "text-purple-300", bg: "from-purple-500/10 to-purple-500/0" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Overview</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Dashboard Agent</h1>
          <p className="text-slate-400 mt-1 text-sm">Semua ringkasan listing & lead Anda hari ini.</p>
        </div>
        <Link to="/app/listings/new">
          <Button data-testid="dashboard-new-listing-btn" className="bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Tambah Listing
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
               className={`relative rounded-xl p-5 border border-white/5 bg-gradient-to-br ${k.bg} bg-[#141C2E] overflow-hidden`}>
            <div className="flex items-center justify-between">
              <k.icon className={`w-5 h-5 ${k.accent}`} />
              <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{k.label}</span>
            </div>
            <div className="mt-3 text-3xl font-extrabold tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl bg-[#141C2E] border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-100">Listing Terbaru</h3>
            <Link to="/app/listings" className="text-sm text-orange-400 hover:text-orange-300" data-testid="see-all-listings">Lihat semua →</Link>
          </div>
          {recent.length === 0 ? (
            <EmptyState title="Belum ada listing" cta="Tambah listing pertama" to="/app/listings/new" />
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {recent.map((l) => <MiniListing key={l.id} l={l} />)}
            </div>
          )}
        </div>

        <div className="rounded-xl bg-[#141C2E] border border-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><Flame className="w-4 h-4 text-red-400" /> Hot Leads</h3>
            <Link to="/app/leads" className="text-sm text-orange-400 hover:text-orange-300" data-testid="see-all-leads">Lihat semua →</Link>
          </div>
          {hotLeads.length === 0 ? (
            <EmptyState title="Belum ada hot lead" cta="Tambah lead" to="/app/leads" />
          ) : (
            <ul className="space-y-3">
              {hotLeads.map((lead) => (
                <li key={lead.id} className="p-3 rounded-lg bg-[#0D1322] border border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-slate-100">{lead.name}</div>
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 font-semibold">HOT</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{lead.preferred_location || "—"} · {lead.budget_max ? formatRupiah(lead.budget_max) : "Budget bebas"}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniListing({ l }) {
  const img = l.photos?.[0];
  return (
    <Link to={`/app/listings/${l.id}`} className="group rounded-xl overflow-hidden border border-white/5 bg-[#0D1322] hover:border-orange-500/30 transition">
      <div className="aspect-video bg-slate-800 overflow-hidden">
        {img ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-8 h-8" /></div>
        )}
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm text-slate-100 line-clamp-1">{l.title}</div>
        <div className="text-xs text-slate-400 line-clamp-1">{l.location}</div>
        <div className="text-sm text-orange-400 font-semibold mt-1">{formatRupiah(l.price)}</div>
      </div>
    </Link>
  );
}

function EmptyState({ title, cta, to }) {
  return (
    <div className="py-10 text-center">
      <div className="text-slate-400 text-sm">{title}</div>
      <Link to={to}>
        <Button className="mt-3 bg-white/5 hover:bg-white/10 border border-white/10" data-testid="empty-cta">{cta}</Button>
      </Link>
    </div>
  );
}
